import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import TransactionModal from './TransactionModal';
import { formatCurrency, formatMoney, formatDate } from '../../utils/formatters';
import { toBRL } from '../../utils/exchange';
import { toast } from '../shared/Toast';
import { parseOFX, parseCSV } from '../../utils/importers';
import { fmtDateBR } from '../../utils/cards';

const catBgColors = { alim:'#FAEEDA', transp:'#E6F1FB', moradia:'#FCEBEB', saude:'#FCEBEB', sal:'#EAF3DE', free:'#EAF3DE', inv:'#EAF3DE', contas:'#E6F1FB', lazer:'#FBEAF0', cartao:'#FBEAF0', educ:'#EAF3DE', default:'#F1EFE8' };
const catIconColors = { alim:'#854F0B', transp:'#185FA5', moradia:'#e24b4a', saude:'#e24b4a', sal:'#3B6D11', free:'#3B6D11', inv:'#3B6D11', contas:'#378ADD', lazer:'#993556', cartao:'#A32D2D', educ:'#3B6D11', default:'#888780' };

function OFXImportModal({ onClose, onImport }) {
  const { state } = useApp();
  const [txList, setTxList] = useState([]);
  const [fileName, setFileName] = useState('');
  const [accountId, setAccountId] = useState(state.accounts[0]?.id || '');
  const [step, setStep] = useState('upload'); // upload | preview
  const [bulkType, setBulkType] = useState('expense');
  const [bulkCat, setBulkCat] = useState('other-ex');
  const fileRef = useRef();

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    const isCSV = file.name.toLowerCase().endsWith('.csv');
    const reader = new FileReader();
    reader.onload = (ev) => {
      const parsed = isCSV ? parseCSV(ev.target.result) : parseOFX(ev.target.result);
      if (parsed.length === 0) {
        toast.error(`Nenhuma transação encontrada no arquivo ${isCSV ? 'CSV' : 'OFX'}.`);
        return;
      }
      setTxList(parsed.map(t => ({ ...t, selected: true })));
      setStep('preview');
    };
    reader.readAsText(file, 'latin1');
  };

  const toggle = (i) => setTxList(list => list.map((t, j) => j === i ? { ...t, selected: !t.selected } : t));
  const allSelected = txList.every(t => t.selected);
  const toggleAll = () => setTxList(list => list.map(t => ({ ...t, selected: !allSelected })));
  const selectedCount = txList.filter(t => t.selected).length;

  const updateRow = (i, patch) => setTxList(list => list.map((t, j) => j === i ? { ...t, ...patch } : t));

  // Ao trocar o tipo (manual ou em lote), a categoria some se não existir pra esse tipo — reseta pra primeira compatível
  const catForType = (type) => state.categories.find(c => c.type === type)?.id || 'other-ex';

  const applyBulkType = () => setTxList(list => list.map(t => t.selected
    ? { ...t, type: bulkType, categoryId: state.categories.some(c => c.id === t.categoryId && c.type === bulkType) ? t.categoryId : catForType(bulkType) }
    : t));
  // Só aplica a categoria nas linhas marcadas que já são do mesmo tipo do
  // dropdown de categoria (senão criaria categoria de despesa numa receita, ou
  // vice-versa) — por isso o dropdown de categoria também é filtrado por bulkType.
  const applyBulkCat = () => {
    const affected = txList.filter(t => t.selected && t.type === bulkType).length;
    if (affected === 0) { toast.error(`Nenhuma linha marcada é do tipo "${bulkType === 'income' ? 'Receita' : 'Despesa'}". Aplique o tipo primeiro.`); return; }
    setTxList(list => list.map(t => (t.selected && t.type === bulkType) ? { ...t, categoryId: bulkCat } : t));
  };

  const doImport = () => {
    const selected = txList.filter(t => t.selected).map(({ selected: _, fitid: __, ...t }) => ({ ...t, accountId }));
    if (selected.length === 0) { toast.error('Selecione ao menos uma transação.'); return; }
    onImport(selected);
    onClose();
    toast.success(`${selected.length} transações importadas com sucesso!`);
  };

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 640 }}>
        <div className="modal-handle"></div>
        <div className="modal-title">
          <span><i className="fas fa-file-import" style={{ marginRight: 8, color: '#378ADD' }}></i>Importar Extrato OFX</span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {step === 'upload' && (
          <div>
            <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 0, marginBottom: 20 }}>
              Importe o extrato bancário no formato OFX. Todos os bancos brasileiros oferecem esse formato.
            </p>
            <div
              onClick={() => fileRef.current?.click()}
              style={{
                border: '2px dashed var(--border)', borderRadius: 12, padding: '40px 24px',
                textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#378ADD'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
            >
              <i className="fas fa-cloud-arrow-up" style={{ fontSize: 32, color: 'var(--muted)', marginBottom: 12 }}></i>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Clique para selecionar o arquivo</div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>Arquivos .OFX ou .CSV (todos os bancos)</div>
              <input ref={fileRef} type="file" accept=".ofx,.OFX,.csv,.CSV" style={{ display: 'none' }} onChange={handleFile} />
            </div>
            <div style={{ marginTop: 16, padding: '10px 14px', background: 'var(--surface2)', borderRadius: 8, fontSize: 12, color: 'var(--muted)' }}>
              <i className="fas fa-circle-info" style={{ marginRight: 6, color: '#378ADD' }}></i>
              Para exportar o OFX: acesse seu Internet Banking → Extratos → Exportar → Formato OFX
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                <i className="fas fa-file" style={{ marginRight: 6 }}></i>{fileName} — {txList.length} transações encontradas
              </div>
              <div className="form-group" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <label className="form-label" style={{ margin: 0, whiteSpace: 'nowrap' }}>Conta:</label>
                <select className="form-control" value={accountId} onChange={e => setAccountId(e.target.value)} style={{ padding: '5px 8px', fontSize: 12 }}>
                  {state.accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            </div>

            <p style={{ color: 'var(--muted)', fontSize: 12, marginTop: 0, marginBottom: 10 }}>
              A gente identifica entradas e saídas pelo sinal do valor, mas você pode ajustar o tipo e a categoria de cada linha abaixo.
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--surface2)', borderRadius: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <input type="checkbox" checked={allSelected} onChange={toggleAll} id="ofx-all" style={{ cursor: 'pointer' }} />
              <label htmlFor="ofx-all" style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Selecionar todas ({selectedCount}/{txList.length})
              </label>
            </div>

            {selectedCount > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: 10, background: 'rgba(201,162,39,0.08)', border: '1px solid rgba(201,162,39,0.3)', borderRadius: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--gold)', whiteSpace: 'nowrap' }}>
                  Nas {selectedCount} linha{selectedCount !== 1 ? 's' : ''} marcada{selectedCount !== 1 ? 's' : ''}:
                </span>
                <select className="form-control" value={bulkType} onChange={e => { const nt = e.target.value; setBulkType(nt); setBulkCat(catForType(nt)); }} style={{ width: 'auto', padding: '4px 8px', fontSize: 12 }}>
                  <option value="income">Receita</option>
                  <option value="expense">Despesa</option>
                </select>
                <button className="btn btn-ghost btn-sm" onClick={applyBulkType}>Aplicar tipo</button>
                <span style={{ color: 'var(--border)' }}>|</span>
                <select className="form-control" value={bulkCat} onChange={e => setBulkCat(e.target.value)} style={{ width: 'auto', padding: '4px 8px', fontSize: 12 }}>
                  {state.categories.filter(c => c.type === bulkType).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button className="btn btn-ghost btn-sm" onClick={applyBulkCat}>Aplicar categoria (só nas do tipo {bulkType === 'income' ? 'Receita' : 'Despesa'})</button>
              </div>
            )}

            <div style={{ maxHeight: 340, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {txList.map((tx, i) => {
                const catsForType = state.categories.filter(c => c.type === tx.type);
                return (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                    borderRadius: 8,
                    background: tx.selected ? (tx.type === 'income' ? '#f0fdf4' : '#fff5f5') : 'var(--surface2)',
                    border: `1px solid ${tx.selected ? (tx.type === 'income' ? '#bbf7d0' : '#fecaca') : 'transparent'}`,
                    opacity: tx.selected ? 1 : 0.5, transition: 'all 0.1s',
                  }}>
                    <input type="checkbox" checked={tx.selected} onChange={() => toggle(i)} style={{ flexShrink: 0, cursor: 'pointer' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tx.description}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>{tx.date}</div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <select
                          className="form-control"
                          value={tx.type}
                          onClick={e => e.stopPropagation()}
                          onChange={e => {
                            const newType = e.target.value;
                            const keepCat = state.categories.some(c => c.id === tx.categoryId && c.type === newType);
                            updateRow(i, { type: newType, categoryId: keepCat ? tx.categoryId : catForType(newType) });
                          }}
                          style={{ padding: '3px 6px', fontSize: 11, width: 'auto' }}
                        >
                          <option value="income">Receita</option>
                          <option value="expense">Despesa</option>
                        </select>
                        <select
                          className="form-control"
                          value={tx.categoryId}
                          onClick={e => e.stopPropagation()}
                          onChange={e => updateRow(i, { categoryId: e.target.value })}
                          style={{ padding: '3px 6px', fontSize: 11, width: 'auto', maxWidth: 150 }}
                        >
                          {catsForType.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: tx.type === 'income' ? '#1d9e75' : '#e24b4a', flexShrink: 0 }}>
                      {tx.type === 'income' ? '+' : '-'}{formatCurrency(tx.value)}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn btn-ghost" onClick={() => setStep('upload')} style={{ flex: 1, justifyContent: 'center' }}>
                <i className="fas fa-arrow-left"></i> Voltar
              </button>
              <button className="btn btn-primary" onClick={doImport} style={{ flex: 2, justifyContent: 'center' }}>
                <i className="fas fa-file-import"></i> Importar {selectedCount} transações
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Transactions() {
  const { state, dispatch } = useApp();
  const [statusFilter, setStatusFilter] = useState('all'); // all | paid | pending | overdue
  const [typeFilter, setTypeFilter] = useState('all');     // all | income | expense
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [editTx, setEditTx] = useState(null);
  const [showOFX, setShowOFX] = useState(false);
  const [accountFilter, setAccountFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue]  = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [sortBy, setSortBy] = useState('date');   // date | description | value
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const today = new Date();
  const [periodYear, setPeriodYear] = useState(today.getFullYear());
  const [periodMonth, setPeriodMonth] = useState(today.getMonth()); // 0-based
  const [periodMode, setPeriodMode] = useState('month'); // month | all | custom
  const allPeriods = periodMode === 'all';
  const [customFrom, setCustomFrom] = useState(today.toISOString().slice(0, 10));
  const [customTo, setCustomTo] = useState(today.toISOString().slice(0, 10));

  const monthNames = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

  const prevMonth = () => {
    if (periodMonth === 0) { setPeriodMonth(11); setPeriodYear(y => y - 1); }
    else setPeriodMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (periodMonth === 11) { setPeriodMonth(0); setPeriodYear(y => y + 1); }
    else setPeriodMonth(m => m + 1);
  };

  const cats = useMemo(() => Object.fromEntries(state.categories.map(c => [c.id, c])), [state.categories]);

  const filtered = useMemo(() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    let list = [...state.transactions];
    if (periodMode === 'month') {
      const mm = String(periodMonth + 1).padStart(2, '0');
      const prefix = `${periodYear}-${mm}`;
      list = list.filter(t => t.date && t.date.startsWith(prefix));
    } else if (periodMode === 'custom' && customFrom && customTo) {
      list = list.filter(t => t.date && t.date >= customFrom && t.date <= customTo);
    }
    if (typeFilter !== 'all') list = list.filter(t => t.type === typeFilter);
    if (statusFilter === 'paid') list = list.filter(t => t.status === 'paid');
    else if (statusFilter === 'pending') list = list.filter(t => t.status === 'pending');
    else if (statusFilter === 'overdue') list = list.filter(t => t.status === 'pending' && t.date < todayIso);
    if (accountFilter !== 'all') {
      if (accountFilter.startsWith('card:')) {
        const cardId = accountFilter.replace('card:', '');
        list = list.filter(t => t.cardId === cardId);
      } else {
        list = list.filter(t => t.accountId === accountFilter && !t.cardId);
      }
    }
    if (search.trim()) list = list.filter(t => t.description.toLowerCase().includes(search.toLowerCase()));
    if (catFilter !== 'all') list = list.filter(t => t.categoryId === catFilter);
    if (tagFilter !== 'all') list = list.filter(t => (t.tags || []).includes(tagFilter));
    if (minValue !== '') list = list.filter(t => t.value >= parseFloat(minValue));
    if (maxValue !== '') list = list.filter(t => t.value <= parseFloat(maxValue));
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (sortBy === 'value') return (a.value - b.value) * dir;
      if (sortBy === 'description') return a.description.localeCompare(b.description) * dir;
      return (a.date || '').localeCompare(b.date || '') * dir;
    });
    return list;
  }, [state.transactions, statusFilter, typeFilter, search, periodYear, periodMonth, periodMode, customFrom, customTo, accountFilter, catFilter, tagFilter, minValue, maxValue, sortBy, sortDir]);

  // Volta pra página 1 sempre que qualquer filtro muda
  useEffect(() => { setPage(1); }, [statusFilter, typeFilter, search, periodYear, periodMonth, periodMode, customFrom, customTo, accountFilter, catFilter, tagFilter, minValue, maxValue, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Tags únicas usadas em qualquer transação (para o filtro)
  const allTags = useMemo(() => {
    const s = new Set();
    state.transactions.forEach(t => (t.tags || []).forEach(tag => s.add(tag)));
    return [...s].sort();
  }, [state.transactions]);

  // Totais do rodapé sempre em R$ — transações em US$ entram convertidas pelo câmbio do dia
  const totalIncome = filtered.filter(t => t.type === 'income').reduce((s, t) => s + toBRL(t.value, t.currency, state.usdRate), 0);
  const totalExpense = filtered.filter(t => t.type === 'expense').reduce((s, t) => s + toBRL(t.value, t.currency, state.usdRate), 0);

  const exportCSV = () => {
    const headers = ['Data', 'Data da compra', 'Descrição', 'Tipo', 'Categoria', 'Valor', 'Status', 'Conta', 'Cartão'];
    const rows = filtered.map(t => [
      t.date,
      t.purchaseDate || '',
      `"${t.description.replace(/"/g, '""')}"`,
      t.type === 'income' ? 'Receita' : 'Despesa',
      cats[t.categoryId]?.name || '',
      t.value.toFixed(2).replace('.', ','),
      t.status === 'paid' ? 'Pago' : 'Pendente',
      state.accounts.find(a => a.id === t.accountId)?.name || '',
      state.cards.find(c => c.id === t.cardId)?.name || '',
    ]);
    const csv = [headers, ...rows].map(r => r.join(';')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `maw-lancamentos-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success(`${filtered.length} lançamentos exportados!`);
  };

  const deleteTx = (id) => {
    if (window.confirm('Excluir esta transação?')) {
      dispatch({ type: 'DELETE_TRANSACTION', payload: id });
      toast.info('Transação excluída');
    }
  };

  const toggleStatus = (tx) => {
    dispatch({ type: 'TOGGLE_TX_STATUS', payload: tx.id });
    toast.success(tx.status === 'pending' ? 'Marcado como pago!' : 'Marcado como pendente');
  };

  const accountName = (tx) => tx.cardId
    ? (state.cards.find(c => c.id === tx.cardId)?.name || 'Cartão')
    : (state.accounts.find(a => a.id === tx.accountId)?.name || '—');

  const isOverdue = (tx) => tx.status === 'pending' && tx.date < new Date().toISOString().slice(0, 10);

  // Botão de status clicável — o mesmo em tabela (desktop) e lista (mobile)
  const statusPill = (tx) => tx.status === 'paid' ? (
    <button
      onClick={() => toggleStatus(tx)}
      title="Clique pra desfazer"
      style={{ background: 'rgba(29,158,117,0.1)', border: '1px solid rgba(29,158,117,0.35)', color: '#1d9e75', borderRadius: 99, padding: '3px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}
    >
      <i className="fas fa-check"></i> Pago
    </button>
  ) : (
    <button
      onClick={() => toggleStatus(tx)}
      style={{ background: isOverdue(tx) ? '#e24b4a' : 'var(--gold)', border: 'none', color: '#fff', borderRadius: 99, padding: '4px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}
    >
      <i className="fas fa-circle-check"></i> {isOverdue(tx) ? 'Atrasado — Pagar' : 'Marcar pago'}
    </button>
  );

  // Menu ⋮ com Editar/Excluir — o mesmo em tabela e lista
  const rowMenu = (tx) => (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 15, padding: '4px 6px' }}
        onClick={() => setOpenMenuId(openMenuId === tx.id ? null : tx.id)}
        title="Mais opções"
      >
        <i className="fas fa-ellipsis-vertical"></i>
      </button>
      {openMenuId === tx.id && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 199 }} onClick={() => setOpenMenuId(null)} />
          <div style={{ position: 'absolute', right: 0, top: '100%', zIndex: 200, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: 130, overflow: 'hidden' }}>
            <button
              onClick={() => { setEditTx(tx); setModal('edit'); setOpenMenuId(null); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text)', textAlign: 'left' }}
            >
              <i className="fas fa-pen" style={{ width: 14, color: 'var(--muted)' }}></i> Editar
            </button>
            <button
              onClick={() => { deleteTx(tx.id); setOpenMenuId(null); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', background: 'none', border: 'none', borderTop: '1px solid var(--border)', cursor: 'pointer', fontSize: 13, color: '#e24b4a', textAlign: 'left' }}
            >
              <i className="fas fa-trash" style={{ width: 14 }}></i> Excluir
            </button>
          </div>
        </>
      )}
    </div>
  );

  const clickSort = (id) => {
    if (sortBy === id) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(id); setSortDir(id === 'date' ? 'desc' : 'asc'); }
  };
  const sortIcon = (id) => sortBy === id
    ? <i className={`fas fa-caret-${sortDir === 'asc' ? 'up' : 'down'}`} style={{ marginLeft: 4 }}></i>
    : null;

  const filterLabelStyle = { fontSize: 11, fontWeight: 600, color: 'var(--muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' };
  const filterControlStyle = { fontSize: 13, padding: '7px 10px' };

  return (
    <div className="page-enter">
      <div className="row" style={{ marginBottom: 14 }}>
        <span style={{ fontSize: 17, fontWeight: 600 }}>Transações</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowOFX(true)} title="Importar extrato OFX ou CSV">
            <i className="fas fa-file-import"></i> Importar
          </button>
          {filtered.length > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={exportCSV} title="Exportar para Excel">
              <i className="fas fa-file-excel"></i> Exportar
            </button>
          )}
          <button className="btn btn-primary btn-sm" onClick={() => { setEditTx(null); setModal('new'); }}>
            <i className="fas fa-plus"></i> Nova
          </button>
        </div>
      </div>

      {/* Painel de filtros */}
      <div className="card" style={{ padding: '14px 16px', marginBottom: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 10 }}>
          <div>
            <label style={filterLabelStyle}>Status</label>
            <select className="form-control" style={filterControlStyle} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">Todos</option>
              <option value="paid">Pago</option>
              <option value="pending">Pendente</option>
              <option value="overdue">Atrasado</option>
            </select>
          </div>
          <div>
            <label style={filterLabelStyle}>Tipo</label>
            <select className="form-control" style={filterControlStyle} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
              <option value="all">Todos</option>
              <option value="income">Receita</option>
              <option value="expense">Despesa</option>
            </select>
          </div>
          <div>
            <label style={filterLabelStyle}>Conta</label>
            <select className="form-control" style={filterControlStyle} value={accountFilter} onChange={e => setAccountFilter(e.target.value)}>
              <option value="all">Todas</option>
              {state.accounts.filter(a => a.active).map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
              {state.cards.length > 0 && (
                <optgroup label="── Cartões ──">
                  {state.cards.map(c => (
                    <option key={c.id} value={`card:${c.id}`}>{c.name}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
          <div>
            <label style={filterLabelStyle}>Categoria</label>
            <select className="form-control" style={filterControlStyle} value={catFilter} onChange={e => setCatFilter(e.target.value)}>
              <option value="all">Todas</option>
              {state.categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {allTags.length > 0 && (
            <div>
              <label style={filterLabelStyle}>Tags</label>
              <select className="form-control" style={filterControlStyle} value={tagFilter} onChange={e => setTagFilter(e.target.value)}>
                <option value="all">Todas</option>
                {allTags.map(t => <option key={t} value={t}>#{t}</option>)}
              </select>
            </div>
          )}
          <div>
            <label style={filterLabelStyle}>Período</label>
            <select className="form-control" style={filterControlStyle} value={periodMode} onChange={e => setPeriodMode(e.target.value)}>
              <option value="month">Por mês</option>
              <option value="custom">Intervalo de datas</option>
              <option value="all">Todo o histórico</option>
            </select>
          </div>
        </div>

        {periodMode === 'month' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <button className="btn btn-ghost btn-sm" onClick={prevMonth} style={{ padding: '4px 10px', minWidth: 32 }}>
              <i className="fas fa-chevron-left"></i>
            </button>
            <div style={{ flex: 1, textAlign: 'center', fontWeight: 600, fontSize: 14 }}>
              {monthNames[periodMonth]} {periodYear}
            </div>
            <button className="btn btn-ghost btn-sm" onClick={nextMonth} style={{ padding: '4px 10px', minWidth: 32 }}>
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        )}

        {periodMode === 'custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={filterLabelStyle}>De</label>
              <input className="form-control" type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={filterControlStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={filterLabelStyle}>Até</label>
              <input className="form-control" type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={filterControlStyle} />
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '3 1 220px', position: 'relative' }}>
            <label style={filterLabelStyle}>Descrição</label>
            <i className="fas fa-search" style={{ position: 'absolute', left: 12, bottom: 11, color: 'var(--muted)', fontSize: 13 }}></i>
            <input className="form-control" style={{ ...filterControlStyle, paddingLeft: 34 }} placeholder="Pesquisar por descrição..." value={search} onChange={e => setSearch(e.target.value)} />
            {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, bottom: 9, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 14 }}><i className="fas fa-times"></i></button>}
          </div>
          <div style={{ flex: '1 1 100px' }}>
            <label style={filterLabelStyle}>Valor mín.</label>
            <input className="form-control" type="number" min="0" step="0.01" placeholder="0,00" value={minValue} onChange={e => setMinValue(e.target.value)} style={filterControlStyle} />
          </div>
          <div style={{ flex: '1 1 100px' }}>
            <label style={filterLabelStyle}>Valor máx.</label>
            <input className="form-control" type="number" min="0" step="0.01" placeholder="9999,00" value={maxValue} onChange={e => setMaxValue(e.target.value)} style={filterControlStyle} />
          </div>
          {(statusFilter !== 'all' || typeFilter !== 'all' || accountFilter !== 'all' || catFilter !== 'all' || tagFilter !== 'all' || search || minValue !== '' || maxValue !== '') && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => { setStatusFilter('all'); setTypeFilter('all'); setAccountFilter('all'); setCatFilter('all'); setTagFilter('all'); setSearch(''); setMinValue(''); setMaxValue(''); }}
              style={{ fontSize: 11, padding: '8px 12px', flexShrink: 0 }}
            >
              <i className="fas fa-times"></i> Limpar filtros
            </button>
          )}
        </div>
      </div>

      {/* Contagem + paginação */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>
          Total de {filtered.length} transaç{filtered.length === 1 ? 'ão' : 'ões'}.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>Itens por página</span>
          <select className="form-control" value={pageSize} onChange={e => setPageSize(parseInt(e.target.value))} style={{ fontSize: 12, padding: '4px 8px', width: 'auto' }}>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>Página {safePage} de {totalPages}</span>
          <button className="btn btn-ghost btn-sm" disabled={safePage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={{ padding: '4px 10px' }}>
            <i className="fas fa-chevron-left"></i>
          </button>
          <button className="btn btn-ghost btn-sm" disabled={safePage >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} style={{ padding: '4px 10px' }}>
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>

      {/* Tabela (desktop) */}
      <div className="card tx-table-wrap" style={{ padding: 0, overflow: 'visible' }}>
        {filtered.length === 0 ? (
          <div className="empty-state"><i className="fas fa-receipt"></i><div>Nenhuma transação encontrada</div></div>
        ) : (
          <table className="tx-table">
            <thead>
              <tr>
                <th onClick={() => clickSort('date')} style={{ cursor: 'pointer' }}>Data{sortIcon('date')}</th>
                <th>Categoria</th>
                <th>Conta</th>
                <th onClick={() => clickSort('description')} style={{ cursor: 'pointer' }}>Descrição{sortIcon('description')}</th>
                <th>Status</th>
                <th onClick={() => clickSort('value')} style={{ cursor: 'pointer', textAlign: 'right' }}>Valor{sortIcon('value')}</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map(tx => {
                const cat = cats[tx.categoryId];
                return (
                  <tr key={tx.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {formatDate(tx.date + 'T12:00:00')}
                      {/* Cartão: a data da linha é o vencimento da fatura — mostra
                          embaixo o dia da compra pra conferir com o extrato do banco */}
                      {tx.purchaseDate && tx.purchaseDate !== tx.date && (
                        <div style={{ fontSize: 10, color: 'var(--muted)' }}>compra {fmtDateBR(tx.purchaseDate).slice(0, 5)}</div>
                      )}
                    </td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                        <span className="tx-icon" style={{ width: 26, height: 26, fontSize: 11, background: catBgColors[tx.categoryId] || catBgColors.default }}>
                          <i className={cat?.icon || 'fas fa-circle'} style={{ color: catIconColors[tx.categoryId] || catIconColors.default }}></i>
                        </span>
                        <span style={{ fontSize: 12.5 }}>{cat?.name || '—'}{tx.subcategory ? ` › ${tx.subcategory}` : ''}</span>
                      </span>
                    </td>
                    <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                      {tx.cardId && <i className="fas fa-credit-card" style={{ marginRight: 5, color: 'var(--muted)', fontSize: 11 }}></i>}
                      {accountName(tx)}
                    </td>
                    <td>
                      <span style={{ fontWeight: 500 }}>{tx.description}</span>
                      {tx.recurrent && <span className="tag tag-purple" style={{ fontSize: 10, marginLeft: 6 }}><i className="fas fa-rotate"></i></span>}
                      {(tx.tags || []).map(tag => (
                        <span key={tag} onClick={() => setTagFilter(tag)} style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: 'var(--gold)', background: 'rgba(201,162,39,0.1)', borderRadius: 99, padding: '1px 7px', cursor: 'pointer', whiteSpace: 'nowrap' }} title={`Filtrar por #${tag}`}>
                          #{tag}
                        </span>
                      ))}
                    </td>
                    <td>{statusPill(tx)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }} className={tx.type === 'income' ? 'text-green' : 'text-red'}>
                      {tx.type === 'income' ? '+' : '-'}{formatMoney(tx.value, tx.currency)}
                    </td>
                    <td>{rowMenu(tx)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Lista (mobile) */}
      <div className="card tx-mobile-list" style={{ padding: '4px 16px' }}>
        {filtered.length === 0 ? (
          <div className="empty-state"><i className="fas fa-receipt"></i><div>Nenhuma transação encontrada</div></div>
        ) : pageItems.map(tx => {
          const cat = cats[tx.categoryId];
          return (
            <div className="tx-item" key={tx.id} style={{ gap: 10 }}>
              <div className="tx-icon" style={{ background: catBgColors[tx.categoryId] || catBgColors.default }}>
                <i className={cat?.icon || 'fas fa-circle'} style={{ color: catIconColors[tx.categoryId] || catIconColors.default }}></i>
              </div>
              <div className="tx-info">
                <div className="tx-desc">
                  {tx.description}
                  {(tx.tags || []).map(tag => (
                    <span key={tag} onClick={(e) => { e.stopPropagation(); setTagFilter(tag); }} style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: 'var(--gold)', background: 'rgba(201,162,39,0.1)', borderRadius: 99, padding: '1px 7px', cursor: 'pointer', whiteSpace: 'nowrap' }} title={`Filtrar por #${tag}`}>
                      #{tag}
                    </span>
                  ))}
                </div>
                <div className="tx-cat">{cat?.name}{tx.subcategory ? ` › ${tx.subcategory}` : ''} · {accountName(tx)}{tx.recurrent && <span className="tag tag-purple" style={{ fontSize: 10, marginLeft: 4 }}><i className="fas fa-rotate"></i></span>}</div>
              </div>
              <div className="tx-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 }}>
                <div className={`tx-amount ${tx.type === 'income' ? 'text-green' : 'text-red'}`}>
                  {tx.type === 'income' ? '+' : '-'}{formatMoney(tx.value, tx.currency)}
                </div>
                <div className="tx-date">
                  {formatDate(tx.date + 'T12:00:00')}
                  {tx.purchaseDate && tx.purchaseDate !== tx.date && ` · compra ${fmtDateBR(tx.purchaseDate).slice(0, 5)}`}
                </div>
                {statusPill(tx)}
              </div>
              {rowMenu(tx)}
            </div>
          );
        })}
      </div>

      {/* Rodapé de totais — sempre sobre o TOTAL filtrado (todas as páginas) */}
      {filtered.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 12 }}>
          <div className="card" style={{ marginBottom: 0, padding: '12px 16px', textAlign: 'center' }}>
            <div className="metric-label">Receitas</div>
            <div style={{ fontWeight: 700, color: 'var(--green)', fontSize: 16 }}>{formatCurrency(totalIncome)}</div>
          </div>
          <div className="card" style={{ marginBottom: 0, padding: '12px 16px', textAlign: 'center' }}>
            <div className="metric-label">Despesas</div>
            <div style={{ fontWeight: 700, color: 'var(--red)', fontSize: 16 }}>{formatCurrency(totalExpense)}</div>
          </div>
          <div className="card" style={{ marginBottom: 0, padding: '12px 16px', textAlign: 'center' }}>
            <div className="metric-label">Resultado</div>
            <div style={{ fontWeight: 700, color: totalIncome - totalExpense >= 0 ? 'var(--green)' : 'var(--red)', fontSize: 16 }}>
              {totalIncome - totalExpense >= 0 ? '+' : ''}{formatCurrency(totalIncome - totalExpense)}
            </div>
          </div>
        </div>
      )}

      {(modal === 'new' || modal === 'edit') && (
        <TransactionModal tx={modal === 'edit' ? editTx : null} onClose={() => { setModal(null); setEditTx(null); }} />
      )}
      {showOFX && (
        <OFXImportModal
          onClose={() => setShowOFX(false)}
          onImport={txs => dispatch({ type: 'IMPORT_TRANSACTIONS', payload: txs })}
        />
      )}
    </div>
  );
}
